import { useSkeletonDelay } from '../../hooks/useSkeletonDelay';
import SkeletonLoader from '../../components/SkeletonLoader';
import React, { useState, useRef } from 'react';
import { useRealtimeUpdates } from '../../hooks/useRealtimeUpdates';
import { config } from '../../config/environment';
import { showErrorToast, showSuccessToast } from '../../utils/toastHelpers';
import {
  Bot,
  Facebook,
  User,
  Save,
  TestTube,
  X,
  Upload,
  Film,
  Send,
  MessageCircle,
  CheckCircle,
  Sparkles,
  Settings
} from 'lucide-react';
import styled, { useTheme } from 'styled-components';
import { useSendBotMessage, useClearBotContext, queryKeys } from '../../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import { useBotStatus } from '../../hooks/useBotStatus';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useProfilePhoto } from '../../hooks/useProfilePhoto';
import { Avatar } from '../../components/Avatar';
import { linguagemApi } from '../../services/api';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[6]};
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing[4]};
`;

const SettingsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[4]};
`;

const SettingsName = styled.h1`
  font-family: ${(props) => props.theme.typography.fontFamily.heading};
  font-size: ${(props) => props.theme.typography.fontSize['4xl']};
  font-weight: ${(props) => props.theme.typography.fontWeight.bold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin: 0 0 ${(props) => props.theme.spacing[2]} 0;
  background: none;
  -webkit-background-clip: initial;
  -webkit-text-fill-color: initial;
  background-clip: initial;
`;

const SettingsDescription = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.lg};
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0;
`;

const SettingsActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[3]};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
  padding: ${(props) => props.theme.spacing[3]} ${(props) => props.theme.spacing[6]};
  border-radius: ${(props) => props.theme.borders.radius.md};
  font-size: ${(props) => props.theme.typography.fontSize.base};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${(props) => props.theme.animations.duration.fast} ${(props) => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  
  ${(props) => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: ${props.theme.colors.primary};
          color: ${props.theme.colors.textPrimary};
          &:hover {
            background: ${props.theme.colors.primaryHover};
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadows.neon};
          }
        `;
      case 'success':
        return `
          background: ${props.theme.colors.success};
          color: ${props.theme.colors.background};
          &:hover {
            background: ${props.theme.colors.success}dd;
            transform: translateY(-1px);
          }
        `;
      case 'warning':
        return `
          background: ${props.theme.colors.warning};
          color: ${props.theme.colors.background};
          &:hover {
            background: ${props.theme.colors.warning}dd;
            transform: translateY(-1px);
          }
        `;
      case 'danger':
        return `
          background: ${props.theme.colors.error};
          color: ${props.theme.colors.background};
          &:hover {
            background: ${props.theme.colors.error}dd;
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          border-color: ${props.theme.colors.border};
          &:hover {
            background: ${props.theme.colors.backgroundSecondary};
            color: ${props.theme.colors.textPrimary};
            border-color: ${props.theme.colors.primary};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SettingsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[6]};
`;

const SettingsSection = styled.div`
  background: ${(props) => props.theme.colors.backgroundSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.lg};
  padding: ${(props) => props.theme.spacing[6]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[3]};
  margin-bottom: ${(props) => props.theme.spacing[6]};
`;

const SectionIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${(props) => props.theme.borders.radius.md};
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.textPrimary};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const SectionTitle = styled.h3`
  font-family: ${(props) => props.theme.typography.fontFamily.heading};
  font-size: ${(props) => props.theme.typography.fontSize.xl};
  font-weight: ${(props) => props.theme.typography.fontWeight.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[4]};
`;

const Label = styled.label`
  display: block;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: ${(props) => props.theme.spacing[2]};
`;

const Input = styled.input`
  width: 100%;
  padding: ${(props) => props.theme.spacing[3]} ${(props) => props.theme.spacing[4]};
  background: ${(props) => props.theme.colors.backgroundTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.md};
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: ${(props) => props.theme.typography.fontSize.base};
  transition: all ${(props) => props.theme.animations.duration.fast} ${(props) => props.theme.animations.easing.easeInOut};

  /* Remove spinners/setas do input type number */
  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.textTertiary};
  }
`;

const SwitchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${(props) => props.theme.spacing[2]};
  margin-top: ${(props) => props.theme.spacing[2]};
  
  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SwitchCard = styled.div<{ $checked: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[2]};
  padding: ${(props) => props.theme.spacing[3]} ${(props) => props.theme.spacing[3]};
  background: ${(props) => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.sm};
  transition: border-color ${(props) => props.theme.animations.duration.fast};
  
  &:hover {
    border-color: ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.textTertiary};
  }
`;

const SwitchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing[2]};
`;

const SwitchLabel = styled.span`
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.normal};
  color: ${(props) => props.theme.colors.textPrimary};
`;

const SwitchDescription = styled.span`
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  color: ${(props) => props.theme.colors.textSecondary};
  line-height: 1.4;
`;

const Switch = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const SwitchInput = styled.input`
  display: none;
`;

const SwitchSlider = styled.div<{ $checked: boolean }>`
  width: 40px;
  height: 22px;
  background: ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.backgroundSecondary};
  border-radius: ${(props) => props.theme.borders.radius.full};
  position: relative;
  transition: all ${(props) => props.theme.animations.duration.fast};
  border: 1px solid ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.border};
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$checked ? '20px' : '2px'};
    width: 16px;
    height: 16px;
    background: ${(props) => props.theme.colors.textPrimary};
    border-radius: 50%;
    transition: left ${(props) => props.theme.animations.duration.fast};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }
`;

const TestSection = styled.div`
  background: linear-gradient(135deg, ${(props) => props.theme.colors.backgroundSecondary} 0%, ${(props) => props.theme.colors.background} 100%);
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.xl};
  padding: ${(props) => props.theme.spacing[8]};
  box-shadow: ${(props) => props.theme.shadows.xl};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${(props) => props.theme.colors.primary}, ${(props) => props.theme.colors.warning}, ${(props) => props.theme.colors.success});
  }
`;

const TestArea = styled.div`
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: ${(props) => props.theme.spacing[8]};
  margin-top: ${(props) => props.theme.spacing[6]};
  
  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const TestInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[4]};
  background: ${(props) => props.theme.colors.backgroundTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.lg};
  padding: ${(props) => props.theme.spacing[5]};
`;

const TestOutput = styled.div`
  background: linear-gradient(to bottom, ${(props) => props.theme.colors.background}, ${(props) => props.theme.colors.backgroundSecondary});
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.xl};
  padding: ${(props) => props.theme.spacing[6]};
  min-height: 500px;
  max-height: 650px;
  overflow-y: auto;
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  white-space: pre-wrap;
  display: flex;
  flex-direction: column;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.1), ${(props) => props.theme.shadows.lg};
  position: relative;
  
  /* Scrollbar personalizado */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.backgroundTertiary};
    border-radius: ${(props) => props.theme.borders.radius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.colors.primary}40;
    border-radius: ${(props) => props.theme.borders.radius.full};
    
    &:hover {
      background: ${(props) => props.theme.colors.primary}60;
    }
  }
`;

const ChatBubble = styled.div<{ $isBot?: boolean }>`
  background: ${props => props.$isBot 
    ? `linear-gradient(135deg, ${props.theme.colors.backgroundSecondary} 0%, ${props.theme.colors.backgroundTertiary} 100%)`
    : `linear-gradient(135deg, ${props.theme.colors.primary}25 0%, ${props.theme.colors.primary}15 100%)`};
  border: 2px solid ${props => props.$isBot 
    ? props.theme.colors.border 
    : props.theme.colors.primary + '50'};
  border-radius: ${props => props.$isBot 
    ? '18px 18px 18px 4px' 
    : '18px 18px 4px 18px'};
  padding: ${(props) => props.theme.spacing[4]} ${(props) => props.theme.spacing[5]};
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: ${(props) => props.theme.typography.fontSize.base};
  line-height: 1.7;
  max-width: 82%;
  align-self: ${props => props.$isBot ? 'flex-start' : 'flex-end'};
  margin-bottom: ${(props) => props.theme.spacing[4]};
  margin-top: ${(props) => props.theme.spacing[3]};
  box-shadow: ${props => props.$isBot
    ? '0 2px 8px rgba(0, 0, 0, 0.08)'
    : `0 4px 12px ${props.theme.colors.primary}20`};
  position: relative;
  animation: slideIn 0.3s ease-out;
  transform-origin: ${props => props.$isBot ? 'left' : 'right'};
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const ChatAvatar = styled.div<{ $isBot?: boolean }>`
  position: absolute;
  ${props => props.$isBot ? 'left: 12px;' : 'right: 12px;'}
  top: -20px;
  width: 36px;
  height: 36px;
  background: ${props => props.$isBot 
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.warning})`
    : `linear-gradient(135deg, ${props.theme.colors.info}, ${props.theme.colors.primary})`};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 2px solid ${(props) => props.theme.colors.background};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TransformBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
  background: ${props => `linear-gradient(135deg, ${props.$color}20, ${props.$color}10)`};
  color: ${props => props.$color};
  padding: ${(props) => props.theme.spacing[2]} ${(props) => props.theme.spacing[3]};
  border-radius: ${(props) => props.theme.borders.radius.full};
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  font-weight: ${(props) => props.theme.typography.fontWeight.semibold};
  border: 1.5px solid ${props => props.$color + '40'};
  box-shadow: 0 2px 4px ${props => props.$color + '15'};
  transition: all ${(props) => props.theme.animations.duration.fast};
  animation: fadeIn 0.4s ease-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${props => props.$color + '25'};
  }
  
  &::before {
    content: '✨';
    font-size: 12px;
  }
`;

const ScenarioButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing[2]};
  padding: ${(props) => props.theme.spacing[3]} ${(props) => props.theme.spacing[4]};
  background: ${props => props.$active 
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryHover})`
    : props.theme.colors.backgroundSecondary};
  color: ${props => props.$active 
    ? props.theme.colors.textPrimary 
    : props.theme.colors.textSecondary};
  border: 2px solid ${props => props.$active 
    ? props.theme.colors.primary 
    : props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.lg};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${(props) => props.theme.animations.duration.fast};
  box-shadow: ${props => props.$active 
    ? `0 4px 12px ${props.theme.colors.primary}30` 
    : '0 2px 4px rgba(0, 0, 0, 0.05)'};
  
  &:hover {
    transform: translateY(-2px);
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: ${props => props.$active 
      ? `0 6px 16px ${props.theme.colors.primary}40` 
      : `0 4px 12px ${props.theme.colors.primary}20`};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ChatInputContainer = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, ${(props) => props.theme.colors.backgroundSecondary} 90%, transparent);
  padding: ${(props) => props.theme.spacing[4]};
  border-top: 2px solid ${(props) => props.theme.colors.border};
  display: flex;
  gap: ${(props) => props.theme.spacing[3]};
  align-items: flex-end;
  margin-top: auto;
  backdrop-filter: blur(10px);
`;

const ChatInput = styled.input`
  flex: 1;
  padding: ${(props) => props.theme.spacing[3]} ${(props) => props.theme.spacing[4]};
  background: ${(props) => props.theme.colors.background};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.lg};
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  transition: all ${(props) => props.theme.animations.duration.fast};
  
  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.primary}20;
    outline: none;
  }
  
  &::placeholder {
    color: ${(props) => props.theme.colors.textTertiary};
  }
`;

const SendButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing[3]};
  background: ${props => props.$disabled 
    ? props.theme.colors.backgroundTertiary 
    : `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryHover})`};
  color: ${props => props.$disabled 
    ? props.theme.colors.textTertiary 
    : props.theme.colors.textPrimary};
  border: none;
  border-radius: ${(props) => props.theme.borders.radius.lg};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all ${(props) => props.theme.animations.duration.fast};
  box-shadow: ${props => props.$disabled 
    ? 'none' 
    : `0 4px 12px ${props.theme.colors.primary}30`};
  
  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.$disabled 
      ? 'none' 
      : `0 6px 16px ${props.theme.colors.primary}40`};
  }
  
  &:active {
    transform: ${props => props.$disabled ? 'none' : 'translateY(0)'};
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing[4]};
  background: ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom: 2px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borders.radius.lg} ${(props) => props.theme.borders.radius.lg} 0 0;
  margin: -${(props) => props.theme.spacing[6]} -${(props) => props.theme.spacing[6]} ${(props) => props.theme.spacing[4]};
`;

const PreviewTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[3]};
  font-size: ${(props) => props.theme.typography.fontSize.base};
  font-weight: ${(props) => props.theme.typography.fontWeight.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
`;

const StatusDot = styled.div<{ $active?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$active ? props.theme.colors.success : props.theme.colors.textTertiary};
  animation: ${props => props.$active ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.1);
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 75vh;
  padding: ${(props) => props.theme.spacing[6]};
  text-align: center;
  color: ${(props) => props.theme.colors.textSecondary};
  gap: ${(props) => props.theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${(props) => props.theme.colors.border};
  border-top: 3px solid ${(props) => props.theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;


const BotSettings: React.FC = () => {
  const themeCtx = useTheme();
  const { isLoading: botStatusLoading, isActive: botActive, error: botStatusError } = useBotStatus();
  const { user: googleUser } = useCurrentUser();
  const { profilePhoto } = useProfilePhoto();
  const [botSettings, setBotSettings] = useState({
    name: 'LoopraBot',
    personality: 'Profissional e Comercial',
    responseTime: '2',
    autoResponse: true,
    smartReplies: true,
    contextAwareness: true,
    learningMode: true,
    slangMode: true, // Gírias ativadas por padrão (backend default)
    informalLanguage: true, // Linguagem informal ativada por padrão
    humor: true, // Humor contextual (kkkk) ativado por padrão
    emotionalAwareness: false,
    typingIndicator: true,
    aiValidation: true,
    maxConversations: '100',
    workingHours: '24/7'
  });

  const [testMessage, setTestMessage] = useState('');

  // Estados para chat interativo
  type ChatMessage = {
    id: string;
    text: string;
    isBot: boolean;
    timestamp: Date;
    transforms?: string[];
  };

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [activeScenario, setActiveScenario] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const sendMessageMutation = useSendBotMessage();
  const clearContextMutation = useClearBotContext();

  // Atualização em tempo real
  const queryClient = useQueryClient();
  const handleRealtimeEvent = React.useCallback((event: import('../../hooks/useRealtimeUpdates').RealtimeEvent) => {
    const t = event.type;
    if (t && (t === 'bot_update' || t === 'dashboard_update')) {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      queryClient.refetchQueries({ queryKey: queryKeys.dashboardStats, type: 'active' });
    }
  }, [queryClient]);
  useRealtimeUpdates({ onEvent: handleRealtimeEvent });

  const handleSaveSettings = async () => {
    if (!googleUser) {
      showErrorToast('Usuário do Google não encontrado. Faça login novamente.', themeCtx);
      return;
    }

    try {
      // Salva as configurações no localStorage usando o UID do Google
      const userId = googleUser.uid;
      const settingsData = {
        botSettings,
        lastUpdated: new Date().toISOString(),
        userInfo: {
          uid: googleUser.uid,
          email: googleUser.email,
          displayName: googleUser.displayName,
          photoURL: googleUser.photoURL
        }
      };

      localStorage.setItem(`loopra_settings_${userId}`, JSON.stringify(settingsData));

      // Sincronizar configurações de linguagem com o backend
      try {
        // Sincroniza gírias (slangMode)
        await linguagemApi.setGlobalSlang(botSettings.slangMode);
        console.log('✅ Configuração de gírias sincronizada com o backend');
        
        // Linguagem informal e humor são parte do sistema de gírias
        // Se qualquer um estiver ativo, ativa o sistema completo de humanização
        const linguagemAtiva = botSettings.informalLanguage || botSettings.humor || botSettings.slangMode;
        if (linguagemAtiva !== botSettings.slangMode) {
          await linguagemApi.setGlobalSlang(linguagemAtiva);
          console.log('✅ Sistema de linguagem humanizada sincronizado');
        }
      } catch (apiErr) {
        console.error('⚠️ Erro ao sincronizar linguagem com o backend:', apiErr);
        // Não bloqueia o salvamento local
      }

      // Sincronizar configurações do bot (nome, personalidade, etc) com o backend
      try {
        const response = await fetch('http://localhost:8081/api/bot-config/save', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: googleUser.uid,
            config: botSettings
          })
        });
        
        if (response.ok) {
          console.log('✅ Configurações do bot sincronizadas com o backend');
          console.log('📝 Nome do bot salvo:', botSettings.name);
        } else {
          console.warn('⚠️ Erro ao sincronizar configurações do bot com o backend');
        }
      } catch (apiErr) {
        console.error('⚠️ Erro ao sincronizar configurações do bot:', apiErr);
        // Não bloqueia o salvamento local
      }

      showSuccessToast('Configurações do bot salvas com sucesso!', themeCtx);
    } catch (err) {
      showErrorToast('Erro ao salvar configurações: ' + (err instanceof Error ? err.message : String(err)), themeCtx);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testMessage.trim()) {
      showErrorToast('Digite uma mensagem para testar', themeCtx);
      return;
    }

    // Adiciona mensagem do usuário ao histórico
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: testMessage,
      isBot: false,
      timestamp: new Date()
    };

    setChatHistory(prev => [...prev, userMessage]);
    const currentMessage = testMessage;
    setTestMessage(''); // Limpa o input
    setIsTyping(true);

    try {
      // Envia apenas a mensagem sem contexto pesado - deixa o backend gerenciar o histórico
      const result = await sendMessageMutation.mutateAsync({
        to: '5511999999999',
        message: currentMessage,
        userId: googleUser?.uid || 'test'
      });
      
      // Formata a resposta do bot
      const botResponse = result.data || 'Sem resposta do bot';

      // Detecta transformações aplicadas baseado nas configs ATIVAS
      const transforms: string[] = [];

      // Verifica linguagem informal
      if (botSettings.informalLanguage) {
        const informalPatterns = ['pra', 'vc', 'tá', 'tua', 'né', 'oii', 'blz'];
        if (informalPatterns.some(p => botResponse.toLowerCase().includes(p))) {
          transforms.push('Linguagem Informal');
        }
      }

      // Verifica humor
      if (botSettings.humor) {
        const humorPatterns = ['kkkk', 'kkk', 'rsrs', 'haha', '😂', '😊', '🙂'];
        if (humorPatterns.some(p => botResponse.toLowerCase().includes(p))) {
          transforms.push('Humor');
        }
      }

      // Verifica gírias
      if (botSettings.slangMode) {
        const slangPatterns = ['mt', 'tlgd', 'dms', 'blz', 'tmj', 'vlw'];
        if (slangPatterns.some(p => botResponse.toLowerCase().includes(p))) {
          transforms.push('Gírias');
        }
      }
      
      // Adiciona resposta do bot ao histórico
      const botMessage: ChatMessage = {
        id: `bot-${Date.now()}`,
        text: botResponse,
        isBot: true,
        timestamp: new Date(),
        transforms: transforms.length > 0 ? transforms : undefined
      };
      
      setTimeout(() => {
        setChatHistory(prev => [...prev, botMessage]);
        setIsTyping(false);
      }, 400); // Simula delay de digitação
      
    } catch (error) {
      console.error('Erro no chat:', error);
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        text: `❌ Erro: ${errorMsg}. Verifique se o backend está rodando.`,
        isBot: true,
        timestamp: new Date()
      };
      
      setChatHistory(prev => [...prev, errorMessage]);
      setIsTyping(false);
      showErrorToast('Erro ao enviar mensagem: ' + errorMsg, themeCtx);
    }
  };
  
  // Função para carregar cenários e gerar conversa com IA em tempo real
  const loadScenario = async (scenarioName: string) => {
    setActiveScenario(scenarioName);
    setChatHistory([]);
    setIsTyping(true);
    
    // Contextos iniciais para cada cenário
    const scenarioContexts: Record<string, { userMessages: string[], context: string }> = {
      'Mensagem Livre': {
        userMessages: [],
        context: 'Início de conversa aberta'
      }
    };
    
    const scenario = scenarioContexts[scenarioName];
    if (!scenario) {
      setIsTyping(false);
      return;
    }
    
    // Se for "Mensagem Livre", mostra saudação imediata sem API
    if (scenarioName === 'Mensagem Livre') {
      // Gera saudação instantânea baseada nas configurações
      let greeting = '';
      const transforms: string[] = [];

      if (botSettings.informalLanguage) {
        greeting = `Oii! Sou a ${botSettings.name.includes('Bot') ? 'Lidiane' : botSettings.name}, me conta qual tua maior dificuldade hoje? 🙂`;
        transforms.push('Linguagem Informal');
      } else {
        greeting = `Olá! Sou ${botSettings.name}. Como posso auxiliá-lo hoje?`;
      }

      const welcomeMessage: ChatMessage = {
        id: 'bot-welcome',
        text: greeting,
        isBot: true,
        timestamp: new Date(),
        transforms: transforms.length > 0 ? transforms : undefined
      };

      setChatHistory([welcomeMessage]);
      setIsTyping(false);
      return;
    }
    
    // Para outros cenários, gera conversa progressiva com IA
    // 🔄 TODO: Futuramente, buscar mensagens dos scripts do usuário via ScriptService
    // Endpoint: GET /api/scripts/{userId}/{tipo}
    const messages: ChatMessage[] = [];
    
    for (let i = 0; i < scenario.userMessages.length; i++) {
      const userMsg = scenario.userMessages[i];
      
      // Adiciona mensagem do usuário
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}-${i}`,
        text: userMsg,
        isBot: false,
        timestamp: new Date()
      };
      messages.push(userMessage);
      setChatHistory([...messages]);
      
      // Aguarda 500ms para simular digitação do usuário
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Gera resposta do bot com IA
      try {
        const result = await sendMessageMutation.mutateAsync({
          to: '5511999999999',
          message: userMsg,
          context: scenario.context,
          userId: googleUser?.uid || 'test'
        });
        
        const botResponse = result.data || 'Sem resposta';
        
        // Detecta transformações
        const transforms: string[] = [];
        if (botSettings.informalLanguage && (botResponse.includes('pra') || botResponse.includes('vc') || botResponse.includes('tá'))) {
          transforms.push('Linguagem Informal');
        }
        if (botSettings.humor && botResponse.includes('kkkk')) {
          transforms.push('Humor');
        }
        if (botSettings.slangMode && (botResponse.includes('mt') || botResponse.includes('tlgd'))) {
          transforms.push('Gírias');
        }
        
        const botMessage: ChatMessage = {
          id: `bot-${Date.now()}-${i}`,
          text: botResponse,
          isBot: true,
          timestamp: new Date(),
          transforms: transforms.length > 0 ? transforms : undefined
        };
        
        messages.push(botMessage);
        setChatHistory([...messages]);
        
        // Aguarda 800ms antes da próxima mensagem
        await new Promise(resolve => setTimeout(resolve, 800));
        
      } catch (error) {
        console.error('Erro ao gerar mensagem do cenário:', error);
        // Em caso de erro, usa fallback
        const fallbackMessage: ChatMessage = {
          id: `bot-${Date.now()}-${i}`,
          text: '❌ Erro ao gerar resposta. Verifique se o backend está rodando.',
          isBot: true,
          timestamp: new Date()
        };
        messages.push(fallbackMessage);
        setChatHistory([...messages]);
        break;
      }
    }
    
    setIsTyping(false);
    setTestMessage('');
  };
  
  // Scroll automático para última mensagem
  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isTyping]);

  // 🔄 Limpa preview quando nome do bot muda (para forçar regeneração)
  React.useEffect(() => {
    if (chatHistory.length > 0) {
      setChatHistory([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botSettings.name]);

  // Estado para saber se está logado no Facebook
  const [facebookUser, setFacebookUser] = useState<{ id: string; name: string } | null>(null);

  // Carrega configurações da conta do Google e Facebook
  React.useEffect(() => {
    // Carrega dados do Facebook (apenas para o botão de login)
    const facebookUserJson = localStorage.getItem('facebookUser');
    if (facebookUserJson) {
      try {
        setFacebookUser(JSON.parse(facebookUserJson));
      } catch {
        // Se não conseguir fazer parse, limpa o localStorage
        localStorage.removeItem('facebookUser');
      }
    }

    // Carrega configurações da conta do Google
    if (googleUser?.uid) {
      try {
        const savedSettings = localStorage.getItem(`loopra_settings_${googleUser.uid}`);
        if (savedSettings) {
          const settingsData = JSON.parse(savedSettings);

          if (settingsData.botSettings) {
            setBotSettings(settingsData.botSettings);
            console.log('📂 Configurações carregadas do localStorage. Nome do bot:', settingsData.botSettings.name);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar configurações do Google:', error);
      }
    }
  }, [googleUser]);

  const showSkeleton = useSkeletonDelay(Boolean(botStatusLoading || botStatusError) || !botActive, 1200);
  if (showSkeleton) {
    return (
      <SettingsContainer>
        <SkeletonLoader variant="botSettings" />
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer>
      <SettingsHeader>
        <SettingsTitle>
          <div>
            <SettingsName>Configurações do Bot</SettingsName>
            <SettingsDescription>Configure o comportamento e integração</SettingsDescription>
          </div>
        </SettingsTitle>
        <SettingsActions>
          {facebookUser ? (
            <div style={{
              background: '#1877F3',
              color: '#fff',
              border: '1px solid #1877F3',
              borderRadius: themeCtx.borders.radius.md,
              opacity: 0.7,
              padding: `${themeCtx.spacing[3]} ${themeCtx.spacing[6]}`,
              display: 'flex',
              alignItems: 'center',
              gap: themeCtx.spacing[2],
              fontSize: themeCtx.typography.fontSize.base,
              fontWeight: themeCtx.typography.fontWeight.medium,
            }}>
              <Facebook size={16} />
              <span>Logado como {facebookUser.name}</span>
              <button
                style={{
                  marginLeft: 'auto',
                  background: 'transparent',
                  color: 'rgba(255, 255, 255, 0.6)',
                  border: 'none',
                  borderRadius: 4,
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 1)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.background = 'transparent';
                }}
                onClick={() => {
                  localStorage.removeItem('facebookUser');
                  setFacebookUser(null);
                  import('react-hot-toast').then(({ toast }) => {
                    toast.success('Deslogado do Facebook com sucesso!', {
                      style: {
                        background: themeCtx.colors.backgroundSecondary,
                        color: themeCtx.colors.textPrimary,
                        border: `1px solid ${themeCtx.colors.success}`,
                        borderRadius: themeCtx.borders.radius.lg,
                        boxShadow: themeCtx.shadows.lg,
                      },
                      iconTheme: {
                        primary: themeCtx.colors.success,
                        secondary: themeCtx.colors.backgroundSecondary,
                      },
                    });
                  });
                }}
                title="Deslogar do Facebook"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <ActionButton
              $variant="primary"
              style={{ background: '#1877F3', color: '#fff', borderColor: '#1877F3' }}
              onClick={() => {
                if (!googleUser?.uid) {
                  import('react-hot-toast').then(({ toast }) => {
                    toast.error('Faça login com Google primeiro!', {
                      style: {
                        background: themeCtx.colors.backgroundSecondary,
                        color: themeCtx.colors.textPrimary,
                        border: `1px solid ${themeCtx.colors.error}`,
                        borderRadius: themeCtx.borders.radius.lg,
                        boxShadow: themeCtx.shadows.lg,
                      },
                    });
                  });
                  return;
                }

                const facebookOAuthUrl = `https://www.facebook.com/v16.0/dialog/oauth?client_id=738984502346873&redirect_uri=${encodeURIComponent(config.facebookRedirectUri)}&state=${encodeURIComponent(googleUser.uid)}&scope=business_management,whatsapp_business_management,whatsapp_business_messaging`;
                window.location.href = facebookOAuthUrl;
              }}
            >
              <Facebook size={16} />
              Login com Facebook
            </ActionButton>
          )}
          <ActionButton $variant="primary" onClick={handleSaveSettings}>
            <Save size={16} />
            Salvar Configurações
          </ActionButton>
        </SettingsActions>
      </SettingsHeader>

      <SettingsGrid>
        {/* Bot Configuration Section */}
        <SettingsSection>
          <SectionHeader>
            <SectionIcon $color={themeCtx.colors.primary}>
              <Bot size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Configurações do Bot</SectionTitle>
              <SectionDescription>Personalize o comportamento do seu bot</SectionDescription>
            </div>
          </SectionHeader>

          <FormGroup>
            <Label>Tempo de Resposta (segundos)</Label>
            <Input
              type="number"
              value={botSettings.responseTime}
              onChange={(e) => setBotSettings(prev => ({ ...prev, responseTime: e.target.value }))}
              placeholder="2"
            />
          </FormGroup>

          <SwitchGrid>
            <SwitchCard $checked={botSettings.informalLanguage}>
              <SwitchHeader>
                <SwitchLabel>Linguagem Informal</SwitchLabel>
                <Switch>
                  <SwitchInput
                    type="checkbox"
                    checked={botSettings.informalLanguage}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      setBotSettings(prev => ({ ...prev, informalLanguage: newValue }));
                      // Sincroniza com backend: linguagem informal faz parte do sistema de humanização
                      try {
                        const linguagemAtiva = newValue || botSettings.humor || botSettings.slangMode;
                        await linguagemApi.setGlobalSlang(linguagemAtiva);
                      } catch (err) {
                        console.error('Erro ao sincronizar linguagem informal:', err);
                      }
                    }}
                  />
                  <SwitchSlider $checked={botSettings.informalLanguage} />
                </Switch>
              </SwitchHeader>
              <SwitchDescription>
                Usa contrações naturais: "pra", "vc", "tá" ao invés de formal
              </SwitchDescription>
            </SwitchCard>

            <SwitchCard $checked={botSettings.humor}>
              <SwitchHeader>
                <SwitchLabel>Humor Contextual</SwitchLabel>
                <Switch>
                  <SwitchInput
                    type="checkbox"
                    checked={botSettings.humor}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      setBotSettings(prev => ({ ...prev, humor: newValue }));
                      // Sincroniza com backend: humor (kkkk) faz parte do sistema de humanização
                      try {
                        const linguagemAtiva = botSettings.informalLanguage || newValue || botSettings.slangMode;
                        await linguagemApi.setGlobalSlang(linguagemAtiva);
                      } catch (err) {
                        console.error('Erro ao sincronizar humor:', err);
                      }
                    }}
                  />
                  <SwitchSlider $checked={botSettings.humor} />
                </Switch>
              </SwitchHeader>
              <SwitchDescription>
                Adiciona expressões "Kkkk" em contextos apropriados e ri junto com o cliente
              </SwitchDescription>
            </SwitchCard>

            <SwitchCard $checked={botSettings.contextAwareness}>
              <SwitchHeader>
                <SwitchLabel>Consciência de Contexto</SwitchLabel>
                <Switch>
                  <SwitchInput
                    type="checkbox"
                    checked={botSettings.contextAwareness}
                    onChange={(e) => setBotSettings(prev => ({ ...prev, contextAwareness: e.target.checked }))}
                  />
                  <SwitchSlider $checked={botSettings.contextAwareness} />
                </Switch>
              </SwitchHeader>
              <SwitchDescription>
                Estende o histórico da conversa de forma mais prolongada
              </SwitchDescription>
            </SwitchCard>

            <SwitchCard $checked={botSettings.slangMode}>
              <SwitchHeader>
                <SwitchLabel>Gírias</SwitchLabel>
                <Switch>
                  <SwitchInput
                    type="checkbox"
                    checked={botSettings.slangMode}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      setBotSettings(prev => ({ ...prev, slangMode: newValue }));
                      // Sincroniza com o backend
                      try {
                        // Se ativar gírias, ativa todo o sistema de humanização
                        const linguagemAtiva = newValue || botSettings.informalLanguage || botSettings.humor;
                        await linguagemApi.setGlobalSlang(linguagemAtiva);
                        showSuccessToast(
                          newValue ? 'Gírias ativadas!' : 'Gírias desativadas',
                          themeCtx
                        );
                      } catch (err) {
                        console.error('Erro ao sincronizar gírias:', err);
                        showErrorToast('Erro ao sincronizar com o servidor', themeCtx);
                      }
                    }}
                  />
                  <SwitchSlider $checked={botSettings.slangMode} />
                </Switch>
              </SwitchHeader>
              <SwitchDescription>
                Usa expressões modernas de redes sociais e WhatsApp
              </SwitchDescription>
            </SwitchCard>

          </SwitchGrid>
        </SettingsSection>
      </SettingsGrid>

      {/* Test Section - Preview Profissional */}
      <TestSection>
        <SectionHeader>
          <SectionIcon $color={themeCtx.colors.warning}>
            <TestTube size={20} />
          </SectionIcon>
          <div>
            <SectionTitle>Preview do Bot</SectionTitle>
            <SectionDescription>Visualize como o bot responde com as configurações atuais</SectionDescription>
          </div>
        </SectionHeader>

        {/* Cenários de Teste - Design Profissional */}
        <div style={{ marginBottom: themeCtx.spacing[6] }}>
          <Label style={{
            marginBottom: themeCtx.spacing[3],
            fontSize: themeCtx.typography.fontSize.base,
            fontWeight: themeCtx.typography.fontWeight.semibold,
            display: 'flex',
            alignItems: 'center',
            gap: themeCtx.spacing[2]
          }}>
            <Film size={20} style={{ color: themeCtx.colors.primary }} />
            Iniciar Conversa
          </Label>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: themeCtx.spacing[3]
          }}>
            {[
              { icon: MessageCircle, label: 'Mensagem Livre', name: 'Mensagem Livre' }
            ].map((scenario) => (
              <ScenarioButton
                key={scenario.label}
                type="button"
                $active={activeScenario === scenario.name}
                onClick={() => loadScenario(scenario.name)}
              >
                <scenario.icon size={20} />
                {scenario.label}
              </ScenarioButton>
            ))}
          </div>
        </div>

        <TestArea>
          <TestInput>
            {/* Preview das Configurações Ativas - Design Melhorado */}
            <div style={{
              background: `linear-gradient(135deg, ${themeCtx.colors.backgroundTertiary}, ${themeCtx.colors.backgroundSecondary})`,
              border: `2px solid ${themeCtx.colors.border}`,
              borderRadius: themeCtx.borders.radius.lg,
              padding: themeCtx.spacing[5],
              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                fontSize: themeCtx.typography.fontSize.sm,
                fontWeight: themeCtx.typography.fontWeight.bold,
                color: themeCtx.colors.textPrimary,
                marginBottom: themeCtx.spacing[4],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: themeCtx.spacing[2]
              }}>
                <Settings size={18} style={{ color: themeCtx.colors.primary }} />
                <span>Configurações Ativas</span>
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: themeCtx.spacing[2],
                alignItems: 'center'
              }}>
                {botSettings.informalLanguage && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: themeCtx.spacing[2],
                    padding: `${themeCtx.spacing[2]} ${themeCtx.spacing[3]}`,
                    background: themeCtx.colors.backgroundSecondary,
                    border: `1px solid ${themeCtx.colors.border}`,
                    borderRadius: themeCtx.borders.radius.md,
                    fontSize: themeCtx.typography.fontSize.xs,
                    width: '100%'
                  }}>
                    <CheckCircle size={16} style={{ color: themeCtx.colors.primary }} />
                    <span style={{ color: themeCtx.colors.textPrimary, fontWeight: themeCtx.typography.fontWeight.medium }}>
                      Linguagem Informal
                    </span>
                  </div>
                )}
                {botSettings.humor && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: themeCtx.spacing[2],
                    padding: `${themeCtx.spacing[2]} ${themeCtx.spacing[3]}`,
                    background: themeCtx.colors.backgroundSecondary,
                    border: `1px solid ${themeCtx.colors.border}`,
                    borderRadius: themeCtx.borders.radius.md,
                    fontSize: themeCtx.typography.fontSize.xs,
                    width: '100%'
                  }}>
                    <CheckCircle size={16} style={{ color: themeCtx.colors.warning }} />
                    <span style={{ color: themeCtx.colors.textPrimary, fontWeight: themeCtx.typography.fontWeight.medium }}>
                      Humor Contextual (kkkk)
                    </span>
                  </div>
                )}
                {botSettings.slangMode && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: themeCtx.spacing[2],
                    padding: `${themeCtx.spacing[2]} ${themeCtx.spacing[3]}`,
                    background: themeCtx.colors.backgroundSecondary,
                    border: `1px solid ${themeCtx.colors.border}`,
                    borderRadius: themeCtx.borders.radius.md,
                    fontSize: themeCtx.typography.fontSize.xs,
                    width: '100%'
                  }}>
                    <CheckCircle size={16} style={{ color: themeCtx.colors.success }} />
                    <span style={{ color: themeCtx.colors.textPrimary, fontWeight: themeCtx.typography.fontWeight.medium }}>
                      Gírias e Expressões
                    </span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: themeCtx.spacing[2],
                  padding: `${themeCtx.spacing[2]} ${themeCtx.spacing[3]}`,
                  background: themeCtx.colors.backgroundSecondary,
                  border: `1px solid ${themeCtx.colors.border}`,
                  borderRadius: themeCtx.borders.radius.md,
                  fontSize: themeCtx.typography.fontSize.xs,
                  width: '100%',
                  marginTop: themeCtx.spacing[2]
                }}>
                  <Bot size={16} style={{ color: themeCtx.colors.info }} />
                  <span style={{ color: themeCtx.colors.textPrimary, fontWeight: themeCtx.typography.fontWeight.medium }}>
                    {botSettings.personality}
                  </span>
                </div>
              </div>
            </div>

            {/* Botão de Limpar Chat */}
            <ActionButton
              $variant="secondary"
              onClick={async () => {
                setChatHistory([]);
                setActiveScenario(null);
                setTestMessage('');
                // Limpa o contexto do bot no backend
                try {
                  await clearContextMutation.mutateAsync(googleUser?.uid || 'test');
                } catch (err) {
                  console.log('Contexto limpo localmente');
                }
              }}
              disabled={chatHistory.length === 0}
              style={{ 
                width: '100%',
                padding: `${themeCtx.spacing[3]} ${themeCtx.spacing[4]}`,
                fontSize: themeCtx.typography.fontSize.sm
              }}
            >
              <X size={18} />
              <span>Limpar Conversa</span>
            </ActionButton>
          </TestInput>
          
          <TestOutput>
            {chatHistory.length > 0 ? (
              <>
                {/* Header Profissional do Chat */}
                <PreviewHeader>
                  <PreviewTitle>
                    <StatusDot $active={true} />
                    <MessageCircle size={18} />
                    <span>{activeScenario || 'Conversa ao Vivo'}</span>
                  </PreviewTitle>
                  <div style={{
                    fontSize: themeCtx.typography.fontSize.xs,
                    color: themeCtx.colors.textTertiary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: themeCtx.spacing[2]
                  }}>
                    <span>Online</span>
                  </div>
                </PreviewHeader>

                {/* Renderiza todo o histórico de mensagens */}
                {chatHistory.map((msg) => (
                  <ChatBubble key={msg.id} $isBot={msg.isBot}>
                    <ChatAvatar $isBot={msg.isBot}>
                      {msg.isBot ? (
                        <img src="/icons/loopra_icon_profile.webp" alt="Loopra Bot" />
                      ) : profilePhoto ? (
                        <img src={profilePhoto} alt="Cliente" />
                      ) : googleUser?.photoURL ? (
                        <img src={googleUser.photoURL} alt="Cliente" />
                      ) : (
                        <User size={18} style={{ color: '#fff' }} />
                      )}
                    </ChatAvatar>
                    {msg.text}
                    
                    {/* Badges de Transformações (apenas para mensagens do bot) */}
                    {msg.isBot && msg.transforms && msg.transforms.length > 0 && (
                      <div style={{ 
                        marginTop: themeCtx.spacing[3],
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: themeCtx.spacing[2]
                      }}>
                        {msg.transforms.map((t, i) => (
                          <TransformBadge 
                            key={i} 
                            $color={
                              t === 'Linguagem Informal' ? themeCtx.colors.primary :
                              t === 'Humor' ? themeCtx.colors.warning :
                              themeCtx.colors.success
                            }
                          >
                            ✓ {t}
                          </TransformBadge>
                        ))}
                      </div>
                    )}
                  </ChatBubble>
                ))}
                
                {/* Indicador de digitação */}
                {isTyping && (
                  <ChatBubble $isBot={true}>
                    <ChatAvatar $isBot={true}>
                      <img src="/icons/loopra_icon_profile.webp" alt="Loopra Bot" />
                    </ChatAvatar>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: themeCtx.colors.textSecondary,
                        animation: 'typing 1.4s infinite',
                        animationDelay: '0s'
                      }} />
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: themeCtx.colors.textSecondary,
                        animation: 'typing 1.4s infinite',
                        animationDelay: '0.2s'
                      }} />
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: themeCtx.colors.textSecondary,
                        animation: 'typing 1.4s infinite',
                        animationDelay: '0.4s'
                      }} />
                    </div>
                  </ChatBubble>
                )}
                
                {/* Referência para scroll automático */}
                <div ref={chatEndRef} />
                
                {/* Input para continuar conversa */}
                <ChatInputContainer>
                  <ChatInput
                    type="text"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && testMessage.trim() && !isTyping) {
                        e.preventDefault();
                        handleSendTestMessage();
                      }
                    }}
                    placeholder="Digite sua mensagem..."
                    disabled={isTyping}
                  />
                  <SendButton
                    type="button"
                    onClick={handleSendTestMessage}
                    $disabled={!testMessage.trim() || isTyping}
                    disabled={!testMessage.trim() || isTyping}
                  >
                    {isTyping ? (
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: `2px solid ${themeCtx.colors.textTertiary}`,
                        borderTop: `2px solid ${themeCtx.colors.primary}`,
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                    ) : (
                      <Send size={20} />
                    )}
                  </SendButton>
                </ChatInputContainer>
                
                <style>{`
                  @keyframes typing {
                    0%, 60%, 100% {
                      transform: translateY(0);
                      opacity: 0.7;
                    }
                    30% {
                      transform: translateY(-10px);
                      opacity: 1;
                    }
                  }
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </>
            ) : (
              <>
                {/* Header quando vazio */}
                <PreviewHeader>
                  <PreviewTitle>
                    <StatusDot $active={false} />
                    <MessageCircle size={18} />
                    <span>Aguardando Teste</span>
                  </PreviewTitle>
                  <div style={{
                    fontSize: themeCtx.typography.fontSize.xs,
                    color: themeCtx.colors.textTertiary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: themeCtx.spacing[2]
                  }}>
                    <Bot size={16} />
                    <span>Pronto para teste</span>
                  </div>
                </PreviewHeader>

                {/* Estado vazio melhorado */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: themeCtx.spacing[5],
                  padding: themeCtx.spacing[8]
                }}>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    background: `linear-gradient(135deg, ${themeCtx.colors.primary}20, ${themeCtx.colors.warning}20)`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `3px solid ${themeCtx.colors.border}`,
                    animation: 'float 3s ease-in-out infinite',
                    boxShadow: `0 8px 32px ${themeCtx.colors.primary}15`
                  }}>
                    <MessageCircle size={64} style={{
                      color: themeCtx.colors.primary,
                      opacity: 0.7
                    }} />
                  </div>
                  
                  <div style={{
                    textAlign: 'center',
                    maxWidth: '400px'
                  }}>
                    <div style={{ 
                      fontSize: themeCtx.typography.fontSize.xl,
                      fontWeight: themeCtx.typography.fontWeight.bold,
                      color: themeCtx.colors.textPrimary,
                      marginBottom: themeCtx.spacing[2]
                    }}>
                      Preview Inteligente do Bot
                    </div>
                    <div style={{ 
                      fontSize: themeCtx.typography.fontSize.sm,
                      color: themeCtx.colors.textSecondary,
                      lineHeight: 1.6
                    }}>
                      Escolha um cenário acima ou digite sua própria mensagem para ver como o bot responderá com as configurações atuais
                    </div>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: themeCtx.spacing[2],
                    width: '100%',
                    maxWidth: '350px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: themeCtx.spacing[2],
                      padding: themeCtx.spacing[2],
                      background: themeCtx.colors.backgroundSecondary,
                      borderRadius: themeCtx.borders.radius.md,
                      fontSize: themeCtx.typography.fontSize.xs,
                      color: themeCtx.colors.textSecondary
                    }}>
                      <Sparkles size={16} style={{ color: themeCtx.colors.primary }} />
                      <span>Veja transformações de linguagem aplicadas</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: themeCtx.spacing[2],
                      padding: themeCtx.spacing[2],
                      background: themeCtx.colors.backgroundSecondary,
                      borderRadius: themeCtx.borders.radius.md,
                      fontSize: themeCtx.typography.fontSize.xs,
                      color: themeCtx.colors.textSecondary
                    }}>
                      <Settings size={16} style={{ color: themeCtx.colors.warning }} />
                      <span>Teste diferentes personalidades</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: themeCtx.spacing[2],
                      padding: themeCtx.spacing[2],
                      background: themeCtx.colors.backgroundSecondary,
                      borderRadius: themeCtx.borders.radius.md,
                      fontSize: themeCtx.typography.fontSize.xs,
                      color: themeCtx.colors.textSecondary
                    }}>
                      <MessageCircle size={16} style={{ color: themeCtx.colors.success }} />
                      <span>Preview em tempo real como chat</span>
                    </div>
                  </div>
                </div>
                
                <style>{`
                  @keyframes float {
                    0%, 100% {
                      transform: translateY(0);
                    }
                    50% {
                      transform: translateY(-10px);
                    }
                  }
                `}</style>
              </>
            )}
          </TestOutput>
        </TestArea>
      </TestSection>
    </SettingsContainer>
  );
};

export default BotSettings;